import React, { useMemo, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import type { MembershipChurchUpdate } from '../types';
import { formatShortDateTime } from '../utils/membershipFormat';
import '../membership.css';

type Filter = 'all' | 'updated' | 'not';

interface Props {
  show: boolean;
  churches: MembershipChurchUpdate[];
  onHide: () => void;
}

const LastUpdatedChurchesModal: React.FC<Props> = ({ show, churches, onHide }) => {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const updatedCount = churches.filter((church) => church.last_updated_at).length;
  const notCount = churches.length - updatedCount;

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return churches
      .filter((church) => (filter === 'all' ? true : filter === 'updated' ? Boolean(church.last_updated_at) : !church.last_updated_at))
      .filter((church) => !term || church.church_name.toLowerCase().includes(term))
      .sort((a, b) => {
        if (Boolean(a.last_updated_at) !== Boolean(b.last_updated_at)) return a.last_updated_at ? -1 : 1;
        return a.church_name.localeCompare(b.church_name);
      });
  }, [churches, search, filter]);

  const pill = (value: Filter, label: string) => (
    <Button
      key={value}
      size="sm"
      variant={filter === value ? 'primary' : 'outline-secondary'}
      className="membership-pill"
      aria-pressed={filter === value}
      onClick={() => setFilter(value)}
    >
      {label}
    </Button>
  );

  return (
    <Modal show={show} onHide={onHide} centered scrollable className="membership-updated-modal">
      <Modal.Header closeButton>
        <div>
          <Modal.Title as="h2" className="h5 mb-0">Last updated by church</Modal.Title>
          <div className="membership-hint">{updatedCount} of {churches.length} churches updated</div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <Form.Control
          type="search"
          placeholder="Search churches"
          aria-label="Search churches"
          value={search}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
          className="mb-2"
        />
        <div className="d-flex flex-wrap gap-2 mb-2">
          {pill('all', `All ${churches.length}`)}
          {pill('updated', `Updated ${updatedCount}`)}
          {pill('not', `Not updated ${notCount}`)}
        </div>
        {visible.length === 0 ? (
          <p className="text-muted mb-0">No churches match</p>
        ) : (
          <ul className="list-unstyled mb-0">
            {visible.map((church) => (
              <li key={church.church_name} className="membership-updated-modal__row">
                <span className="membership-history__name" title={church.church_name}>{church.church_name}</span>
                <span className={church.last_updated_at ? 'membership-updated-modal__date' : 'membership-zero'}>
                  {church.last_updated_at ? formatShortDateTime(church.last_updated_at) : 'Not updated yet'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default LastUpdatedChurchesModal;
